package com.essntl.features.area.ui.addeditarea

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.destructive.EsLargeDestructiveButton
import com.essntl.uikit.components.button.outline.EsSmallOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState
import kotlinx.coroutines.delay

@Composable
fun AddEditAreaScreen(
    state: AddEditAreaState,
    validation: AddEditAreaValidation,
    onEvent: (AddEditAreaEvent) -> Unit,
) {
    var countrySearchText by rememberSaveable { mutableStateOf("") }

    var focusManager by remember { mutableStateOf<FocusManager?>(null) }

    val filteredCountries = remember(
        countrySearchText,
        state.getAllCountries.data.orEmpty(),
    ) {
        state.getAllCountries.data.orEmpty()
            .filter {
                it.name.contains(
                    countrySearchText,
                    ignoreCase = true,
                )
            }
    }

    val isEdit by remember(state.area.value.id) {
        mutableStateOf(
            state.area.value.id.isNotBlank(),
        )
    }

    var isEditEnabled by remember(state.getArea.data?.id) { mutableStateOf(false) }

    LaunchedEffect(state.addEditArea.isSuccess) {
        if (state.addEditArea.isSuccess) {
            isEditEnabled = false
        }
    }

    LaunchedEffect(state.getArea.data) {
        validation.addEditAreaForm.reset()
        validation.addEditAreaForm.name.value = state.getArea.data?.name.orEmpty()
        validation.addEditAreaForm.countryName.value = state.getArea.data?.countryModel?.name.orEmpty()
    }

    LaunchedEffect(state.addCountry.isSuccess) {
        if (state.addCountry.isSuccess)
            state.addCountry.data?.let { c ->
                focusManager?.clearFocus()
                validation.addEditAreaForm.countryName.validate(c.name)
                onEvent(
                    AddEditAreaEvent.UpdateAreaFields(
                        state.area.value.copy(countryModel = c),
                    ),
                )
                countrySearchText = ""
            }
    }

    LazyColumn(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .fillMaxWidth()
            .clip(
                RoundedCornerShape(
                    topStart = 24.dp,
                    topEnd = 24.dp,
                ),
            )
            .padding(
                horizontal = 2.5f.spacing,
                vertical = 1.spacing,
            ),
    ) {
        if (
            state.getArea.isLoading ||
            state.getArea.isFailure ||
            (isEdit && state.getArea.data == null)
        )
            item {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .height(200.dp)
                            .padding(2.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                ) {
                    if (state.getArea.isLoading) {
                        EsSpinner()
                    } else {
                        Text(
                            text = "Something goes wrong!",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p1Medium16,
                        )
                    }
                }
            }
        else {
            item {
                EsInput(
                    value = state.area.value.name.orEmpty(),
                    state = if (!state.addEditArea.errorMessage.isNullOrEmpty())
                        EsInputState.Error else
                        validation.addEditAreaForm.name.state,
                    onValueChange = {
                        validation.addEditAreaForm.name.validate(it)
                        onEvent(
                            AddEditAreaEvent.UpdateAreaFields(
                                state.area.value.copy(
                                    name = it,
                                ),
                            ),
                        )
                    },
                    label = "Area",
                    placeholder = "Enter Area",
                    message = state.addEditArea.errorMessage
                        .orEmpty()
                        .ifEmpty { "This field is required" },
                    enabled = if (isEdit) isEditEnabled else true,
                )

                Spacer(modifier = Modifier.height(1.spacing))
            }

            item {
                EsInputDropdownMenu(
                    items = filteredCountries,
                    selectedItem = state.area.value.countryModel,
                    text = { it.name },
                    onItemSelected = {
                        validation.addEditAreaForm.countryName.validate(it.name)
                        onEvent(
                            AddEditAreaEvent.UpdateAreaFields(
                                state.area.value.copy(countryModel = it),
                            ),
                        )
                    },
                    label = "What country is this area in? " +
                        if (state.area.value.countryModel?.name.isNullOrEmpty())
                            "Please choose a country for the area" else
                            "",
                    placeholder = "Enter Country Name",
                    message = "This field is required",
                    state = validation.addEditAreaForm.countryName.state,
                    loading = state.getAllCountries.isLoading,
                    enabled = if (isEdit)
                        (isEditEnabled && !state.addEditArea.isLoading) else
                        !state.addEditArea.isLoading,
                    topContent = {
                        Spacer(Modifier.height(0.5.spacing))
                        EsInput(
                            value = countrySearchText,
                            onValueChange = {
                                countrySearchText = it
                            },
                            placeholder = "Search",
                            prefix = {
                                EsMediumIcon(res = EsIcons.Search)
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )

                        Spacer(Modifier.height(1.spacing))
                    },
                    emptyContent = { fm ->
                        focusManager = fm

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(1.spacing),
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier
                                .height(50.dp)
                                .padding(1.spacing),
                        ) {
                            Text(
                                text = "Create: ",
                                style = MaterialTheme.esTypography.p2Regular14.copy(
                                    color = MaterialTheme.esColorScheme.textSecondary,
                                ),
                            )

                            EsSmallOutlineButton(
                                text = countrySearchText,
                                loading = state.addCountry.isLoading,
                                enabled = !state.addCountry.isLoading,
                                onClick = {
                                    onEvent(
                                        AddEditAreaEvent.AddCountry(
                                            title = countrySearchText,
                                        ),
                                    )
                                },
                            )
                        }
                    },
                )
            }

            item {
                Spacer(modifier = Modifier.height(3.spacing))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    if (isEdit)
                        EsLargeDestructiveButton(
                            onClick = {
                                onEvent(AddEditAreaEvent.DeleteArea)
                            },
                            text = "Delete Area",
                            suffixIconRes = EsIcons.Trash,
                            loading = state.countLoading || state.deleteArea.isLoading,
                            enabled = state.count.toInt() == 0,
                            modifier = Modifier.weight(1f),
                        )

                    EsLargePrimaryButton(
                        onClick = {
                            if (isEdit && !isEditEnabled) {
                                isEditEnabled = true
                            } else if (validation.addEditAreaForm.validate()) {
                                if (isEdit) {
                                    onEvent(AddEditAreaEvent.EditArea)
                                } else {
                                    onEvent(AddEditAreaEvent.AddArea)
                                }
                            }
                        },
                        text =
                            if (isEdit) {
                                if (isEditEnabled) {
                                    "Update"
                                } else {
                                    "Edit"
                                }
                            } else {
                                "Save Area"
                            },
                        loading = state.addEditArea.isLoading,
                        enabled = if (isEditEnabled)
                            (state.isEditEnabled) else
                            true,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
        }
    }
}